import { useAuthStore } from '@/stores/auth';
import { ref } from 'vue';

const TOUR_STORAGE_KEY = 'bossai-tour-completed';

// Global state to track active tours and queue
const activeTour = ref<string | null>(null);
const tourQueue = ref<Array<{ name: string; callback: () => void }>>([]);

export interface TourStep {
  target: string;
  title: string;
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

// Helper function to get user-specific tour key
const getTourKey = (tourName: string): string => {
  try {
    const authStore = useAuthStore();
    const userId = authStore.user?.id;
    // Include user ID in the key to make tours user-specific
    return userId ? `${TOUR_STORAGE_KEY}-${userId}-${tourName}` : `${TOUR_STORAGE_KEY}-${tourName}`;
  } catch {
    return `${TOUR_STORAGE_KEY}-${tourName}`;
  }
};

export const useTour = () => {
  const hasCompletedTour = (tourName: string): boolean => {
    try {
      const storageKey = getTourKey(tourName);
      const completed = localStorage.getItem(storageKey);
      return completed === 'true';
    } catch {
      return false;
    }
  };

  const markTourCompleted = (tourName: string): void => {
    try {
      const storageKey = getTourKey(tourName);
      localStorage.setItem(storageKey, 'true');
      // Also set a timestamp for when the tour was completed
      localStorage.setItem(`${storageKey}-timestamp`, new Date().toISOString());
    } catch {
      // localStorage not available
    }
  };

  const resetTour = (tourName: string): void => {
    try {
      const storageKey = getTourKey(tourName);
      localStorage.removeItem(storageKey);
      localStorage.removeItem(`${storageKey}-timestamp`);
    } catch {
      // localStorage not available
    }
  };

  // Start a tour with queueing support
  const startTour = (tourName: string, callback: () => void): void => {
    // If there's already an active tour, add this to the queue
    if (activeTour.value) {
      const existing = tourQueue.value.find(t => t.name === tourName);
      if (!existing) {
        tourQueue.value.push({ name: tourName, callback });
      }
      return;
    }

    // Start this tour immediately
    activeTour.value = tourName;
    callback();
  };

  // Mark tour as completed and start next in queue
  const completeTour = (tourName: string): void => {
    markTourCompleted(tourName);

    // Clear active tour
    if (activeTour.value === tourName) {
      activeTour.value = null;
    }

    // Start next tour in queue if any
    if (tourQueue.value.length > 0) {
      const nextTour = tourQueue.value.shift();
      if (nextTour) {
        setTimeout(() => {
          startTour(nextTour.name, nextTour.callback);
        }, 500); // Small delay between tours
      }
    }
  };

  return {
    hasCompletedTour,
    markTourCompleted,
    resetTour,
    startTour,
    completeTour
  };
};
